import type { TFunction } from "i18next";
import { z } from "zod";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MAX_LENGTH_MESSAGE_KEY,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_LENGTH_MESSAGE_KEY,
  PASSWORD_PATTERN,
  PASSWORD_PATTERN_MESSAGE_KEY,
} from "./password-policy";

/**
 * Функция перевода в том виде, в каком её отдаёт `useTranslation`.
 *
 * Схемы объявлены ФАБРИКАМИ, принимающими `t`, а не константами модуля (AD-44).
 * Причина в том, что сообщение схемы — обычная строка, вычисляемая один раз при
 * сборке схемы: до правки эти строки были русскими литералами и печатались на
 * немецком и испанском экранах входа как есть (замер: подписи полей «E-Mail» и
 * «Passwort», кнопка «Anmelden», а под ними «Некорректный формат email»).
 * Схема собирается в момент валидации, внутри обработчика отправки, поэтому
 * берёт язык, действующий на этот момент.
 */
export type TranslateFn = TFunction<"common">;

/**
 * Минимальная длина пароля на ВХОДЕ — намеренно не равна политике регистрации.
 *
 * `PASSWORD_MIN_LENGTH` (8) — требование к НОВОМУ паролю. На входе пароль уже
 * существует, и учётные записи, заведённые до ужесточения политики, короче:
 * подняв порог до восьми, форма перестала бы пропускать их владельцев к самому
 * запросу входа — то есть FE отказал бы там, где BE пустил бы.
 */
export const LOGIN_PASSWORD_MIN_LENGTH = 6;

export function createLoginSchema(t: TranslateFn) {
  return z.object({
    email: z
      .string()
      .min(1, t("auth.validation.emailRequired"))
      .email(t("auth.validation.emailInvalid")),
    password: z
      .string()
      .min(1, t("auth.validation.passwordRequired"))
      .min(
        LOGIN_PASSWORD_MIN_LENGTH,
        t("auth.validation.passwordMin", { count: LOGIN_PASSWORD_MIN_LENGTH }),
      ),
  });
}

export type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

/**
 * Зеркало `@MaxLength(20)` у `referralCode` в BE `RegisterDto`. Длиннее — сервер
 * отвечает 400 на всю регистрацию, поэтому форма останавливает раньше и говорит
 * почему (AD-305: код теперь приходит из ссылки, и человек мог его не вводить).
 */
export const REFERRAL_CODE_MAX_LENGTH = 20;

export function createRegisterSchema(t: TranslateFn) {
  return z
    .object({
      firstName: z
        .string()
        .trim()
        .min(1, t("auth.validation.firstNameRequired"))
        .min(2, t("auth.validation.firstNameMin2")),
      lastName: z
        .string()
        .trim()
        .min(1, t("auth.validation.lastNameRequired"))
        .min(2, t("auth.validation.lastNameMin2")),
      email: z
        .string()
        .min(1, t("auth.validation.emailRequired"))
        .email(t("auth.validation.emailInvalid")),
      // Зеркало единой BE-политики (ADR-AUTH-unified-password-policy) — см. password-policy.ts.
      // Раньше FE проверял min(6) без сложности → BE отвечал «Validation failed» после сабмита.
      password: z
        .string()
        .min(1, t("auth.validation.passwordRequired"))
        .min(
          PASSWORD_MIN_LENGTH,
          t(PASSWORD_MIN_LENGTH_MESSAGE_KEY, { count: PASSWORD_MIN_LENGTH }),
        )
        .max(
          PASSWORD_MAX_LENGTH,
          t(PASSWORD_MAX_LENGTH_MESSAGE_KEY, { count: PASSWORD_MAX_LENGTH }),
        )
        .regex(PASSWORD_PATTERN, t(PASSWORD_PATTERN_MESSAGE_KEY)),
      confirmPassword: z
        .string()
        .min(1, t("auth.validation.confirmPasswordRequired")),
      referralCode: z
        .string()
        .max(
          REFERRAL_CODE_MAX_LENGTH,
          t("auth.validation.referralCodeMax", {
            count: REFERRAL_CODE_MAX_LENGTH,
          }),
        )
        .optional(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth.validation.passwordsMismatch"),
      path: ["confirmPassword"],
    });
}

export type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

/** Extract the first error message per field from a Zod parse result. */
export function extractZodErrors(error: z.ZodError): Record<string, string> {
  const fieldErrors = error.flatten().fieldErrors;
  const result: Record<string, string> = {};
  for (const [key, msgs] of Object.entries(fieldErrors)) {
    if (msgs?.[0]) result[key] = msgs[0];
  }
  return result;
}
